import React, { memo, useCallback, useMemo, useRef, useState } from 'react';

import { createUseStyles, Loader, Modal, Text } from '@Sage/hoi-poi-ui';

import { useIframeEmbedBridge } from '../hooks/useIframeEmbedBridge';
import { DEFAULT_IFRAME_MIN_HEIGHT_PX, DEFAULT_IFRAME_SANDBOX } from '../protocol/embedProtocol';
import EmbedIframe from './EmbedIframe';
import EmbedFooter from './EmbedFooter';
import styles from './ExternalEmbedModal.styles';

const useStyles = createUseStyles(styles, 'ExternalEmbedModal');

const ExternalEmbedModal = memo(
    ({
        allow,
        allowedOrigin,
        footerComponent,
        getAuthContext,
        iframeSrc,
        iframeSizing = 'auto',
        iframeTitle = 'External embed',
        initContext,
        initExtras,
        isOpen,
        loadErrorText = 'Unable to load embedded content.',
        maxIframeHeight,
        mode,
        onClose,
        onEmbedReady,
        onInitError,
        onEmbedNotify,
        renderStateFooter = true,
        sandbox = DEFAULT_IFRAME_SANDBOX,
        size = 'big',
        title,
    }) => {
        const classes = useStyles();
        const iframeRef = useRef(null);
        const [isIframeLoading, setIsIframeLoading] = useState(true);
        const [loadError, setLoadError] = useState(false);
        const isIframeFillSizing = iframeSizing === 'fill';

        const onCloseFromEmbed = useCallback(() => {
            onClose?.();
        }, [onClose]);

        const { iframePixelHeight, iframeState, resetBridge, sendActionToIframe } =
            useIframeEmbedBridge({
                allowedOrigin,
                getAuthContext,
                iframeRef,
                iframeSrc,
                initContext,
                initExtras,
                isActive: isOpen,
                maxIframeHeight,
                mode,
                onCloseFromEmbed,
                onEmbedReady,
                onInitError,
                onNotify: onEmbedNotify,
            });

        const handleIframeLoad = useCallback(() => {
            setIsIframeLoading(false);
            setLoadError(false);
        }, []);

        const handleIframeError = useCallback(() => {
            setIsIframeLoading(false);
            setLoadError(true);
        }, []);

        const handleRequestClose = useCallback(() => {
            onClose?.();
        }, [onClose]);

        const iframeStyle = useMemo(() => {
            if (isIframeFillSizing) {
                return {
                    height: '100%',
                };
            }

            const height = iframePixelHeight ?? DEFAULT_IFRAME_MIN_HEIGHT_PX;

            return {
                height,
            };
        }, [iframePixelHeight, isIframeFillSizing]);

        const handleAfterClose = useCallback(() => {
            setIsIframeLoading(true);
            setLoadError(false);
            resetBridge();
        }, [resetBridge]);

        const resolvedFooterComponent = useMemo(() => {
            if (footerComponent) {
                return typeof footerComponent === 'function'
                    ? footerComponent({
                          iframeState,
                          isIframeLoading,
                          loadError,
                          onRequestClose: handleRequestClose,
                          sendActionToIframe,
                      })
                    : footerComponent;
            }

            if (loadError) return null;

            if (!renderStateFooter) return null;

            if (iframeState?.customFooter) {
                return (
                    <EmbedFooter footer={iframeState.customFooter} onAction={sendActionToIframe} />
                );
            }

            return <EmbedFooter footer={iframeState?.footer} onAction={sendActionToIframe} />;
        }, [
            footerComponent,
            handleRequestClose,
            iframeState,
            isIframeLoading,
            loadError,
            renderStateFooter,
            sendActionToIframe,
        ]);

        const modalProps = useMemo(
            () => ({
                footerComponent: resolvedFooterComponent,
                height: Number.isFinite(maxIframeHeight) ? maxIframeHeight : undefined,
                isOpen,
                onAfterClose: handleAfterClose,
                onRequestClose: handleRequestClose,
                shouldCloseOnEsc: true,
                shouldCloseOnOverlayClick: true,
                size,
                title,
                useCornerClose: true,
                overrides: {
                    content: {
                        style: {
                            flex: 1,
                            ...(isIframeFillSizing
                                ? {
                                      display: 'flex',
                                      minHeight: 0,
                                      overflow: 'hidden',
                                  }
                                : {}),
                        },
                    },
                },
            }),
            [
                handleAfterClose,
                handleRequestClose,
                isIframeFillSizing,
                isOpen,
                maxIframeHeight,
                resolvedFooterComponent,
                size,
                title,
            ],
        );

        const iframeProps = useMemo(
            () => ({
                ref: iframeRef,
                allow,
                className: isIframeFillSizing
                    ? `${classes.iframe} ${classes.iframeFill}`
                    : classes.iframe,
                onError: handleIframeError,
                onLoad: handleIframeLoad,
                sandbox: sandbox,
                src: iframeSrc,
                style: {
                    ...iframeStyle,
                    position: isIframeLoading ? 'absolute' : 'relative',
                    visibility: isIframeLoading ? 'hidden' : 'visible',
                },
                title: iframeTitle,
            }),
            [
                allow,
                classes.iframe,
                classes.iframeFill,
                handleIframeError,
                handleIframeLoad,
                iframeSrc,
                iframeStyle,
                iframeTitle,
                isIframeFillSizing,
                isIframeLoading,
                sandbox,
            ],
        );

        const rootClassName = isIframeFillSizing
            ? `${classes.root} ${classes.rootFill}`
            : classes.root;

        return (
            <Modal {...modalProps}>
                <div className={rootClassName}>
                    {loadError && (
                        <Text className={classes.error} type="body">
                            {loadErrorText}
                        </Text>
                    )}
                    {!loadError && isIframeLoading && (
                        <div className={classes.loader} style={iframeStyle}>
                            <Loader size="huge" />
                        </div>
                    )}
                    {!loadError && iframeSrc && <EmbedIframe {...iframeProps} />}
                </div>
            </Modal>
        );
    },
);

ExternalEmbedModal.displayName = 'ExternalEmbedModal';

export default ExternalEmbedModal;
